import React, { useEffect, useState } from "react";
import portalApi from "../../api/portalClient";
import { useBranding } from "../../context/BrandingContext";
import { PortalDevice } from "../../types";
import { useSort, sortRows, SortableTh } from "../../components/SortableTh";
import { formatDateTime } from "../../utils/datetime";

export default function PortalDevices() {
  const [devices, setDevices] = useState<PortalDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const branding = useBranding();
  const devSort = useSort("hostname", "asc");

  useEffect(() => {
    portalApi
      .get("/api/v1/portal/devices")
      .then((r) => setDevices(r.data))
      .finally(() => setLoading(false));
  }, []);

  const online = devices.filter((d) => d.status === "online").length;
  const pendingReboot = devices.filter((d) => d.pending_reboot).length;

  return (
    <div className="max-w-6xl">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">Your Devices</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Devices being monitored by {branding.company_name}. Contact{" "}
        <a
          href={`mailto:${branding.portal_support_email}`}
          className="text-brand dark:text-brand-light hover:underline font-medium"
        >
          support
        </a>{" "}
        if you see something unexpected here.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard label="Total Devices" value={String(devices.length)} accent="bg-brand" />
        <StatCard label="Online" value={String(online)} accent="bg-green-500" tone="text-green-700 dark:text-green-300" />
        <StatCard
          label="Pending Reboot"
          value={String(pendingReboot)}
          accent="bg-orange-500"
          tone={pendingReboot > 0 ? "text-orange-700 dark:text-orange-300" : "text-gray-900 dark:text-gray-100"}
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">All devices</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
              <tr>
                <SortableTh label="Hostname" sortKey="hostname" sort={devSort.sort} onSort={devSort.toggle} />
                <SortableTh label="Status" sortKey="status" sort={devSort.sort} onSort={devSort.toggle} />
                <SortableTh label="Pending Reboot" sortKey="pending_reboot" sort={devSort.sort} onSort={devSort.toggle} />
                <SortableTh label="Last Seen" sortKey="last_seen" sort={devSort.sort} onSort={devSort.toggle} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading && (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && devices.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                    No devices reported yet.
                  </td>
                </tr>
              )}
              {sortRows(devices, devSort.sort, (d, key) => {
                switch (key) {
                  case "status":
                    return (d.status || "").toLowerCase();
                  case "pending_reboot":
                    return d.pending_reboot ? 1 : 0;
                  case "last_seen":
                    return d.last_seen ? Date.parse(d.last_seen) : 0;
                  default:
                    return (d.hostname || "").toLowerCase();
                }
              }).map((d) => (
                <tr key={d.id} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/40 transition-colors">
                  <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">{d.hostname}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium border ${
                        d.status === "online"
                          ? "bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800"
                          : "bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          d.status === "online" ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                      {d.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs">
                    {d.pending_reboot ? (
                      <span className="text-orange-700 dark:text-orange-300 font-medium">
                        Yes — please reboot when convenient
                      </span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">No</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-500 dark:text-gray-400">
                    {d.last_seen ? formatDateTime(d.last_seen) : "Never"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
  tone,
}: {
  label: string;
  value: string;
  accent: string;
  tone?: string;
}) {
  return (
    <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150">
      <div className={`absolute top-0 left-0 w-1 h-full ${accent}`} />
      <div className="pl-5 pr-4 py-4">
        <div className={`text-3xl font-bold ${tone || "text-gray-900 dark:text-gray-100"}`}>{value}</div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mt-1">
          {label}
        </div>
      </div>
    </div>
  );
}
